import { Injectable } from '@angular/core';
import { ReactiveService } from '@app/classes';
import { TaskFilterTypes, TaskStatusTypes } from '@app/enums';
import { QueryFetcher } from '@app/models/core/query-fetcher.model';
import { Task } from '@app/models/tasks/task.model';
import { AuthService, NotifyService } from '@app/services';
@Injectable()
export class DashboardTasksService extends ReactiveService {
    incompleteUserTasks: Task[] = [];
    completeUserTasks: Task[] = [];

    incompleteCompanyTasks: Task[] = [];
    completeCompanyTasks: Task[] = [];

    currentPage = 1;
    shouldConcat = false;
    totalCompleteUserTasks = 0;

    totalIncompleteCompanyTasks = 0;
    totalCompleteCompanyTasks = 0;

    constructor(
        private notify: NotifyService,
        protected auth: AuthService
    ) {
        super();

        this.auth.onLogout.subscribe(() => {
            this.incompleteUserTasks = [];
            this.completeUserTasks = [];

            this.incompleteCompanyTasks = [];
            this.completeCompanyTasks = [];
        });
    }

    reloadTasks(currentPage: number): Promise<void> {
        this.currentPage = currentPage;
        this.shouldConcat = false;

        return this.loadAllTasks();
    }

    loadMoreTasks(page: number, taskType: TaskStatusTypes, taskFilter: TaskFilterTypes): void {
        this.currentPage = page;
        this.shouldConcat = true;

        if (taskFilter === TaskFilterTypes.myself) {
            if (taskType === TaskStatusTypes.complete) {
                this.loadUserTasks({ isComplete: true });
                return;
            }
            this.loadUserTasks({ isComplete: false });
            return;
        }

        if (taskFilter === TaskFilterTypes.company) {
            if (taskType === TaskStatusTypes.complete) {
                this.loadCompanyTasks({ isComplete: true });
                return;
            }
            this.loadCompanyTasks({ isComplete: false });
            return;
        }
    }

    getUserTasks(taskType: TaskStatusTypes): Task[] {
        if (taskType === TaskStatusTypes.complete) {
            return this.completeUserTasks;
        }
        return this.incompleteUserTasks;
    }

    getCompanyTasks(taskType: TaskStatusTypes): Task[] {
        if (taskType === TaskStatusTypes.complete) {
            return this.completeCompanyTasks;
        }
        return this.incompleteCompanyTasks;
    }

    async loadAllTasks(): Promise<void> {
        this.loading.next(true);
        await Promise.all([
            this.loadUserTasks({ isComplete: false }),
            this.loadUserTasks({ isComplete: true }),
            this.loadCompanyTasks({ isComplete: false }),
            this.loadCompanyTasks({ isComplete: true }),
        ]).finally(() => {
            this.changes.next(null);
            this.loading.next(false);
        });
    }

    private async loadUserTasks(options: { isComplete: boolean } = { isComplete: false }): Promise<void> {
        const { isComplete } = options;

        const query = this.baseQuery.clone();
        return query
            .where('assigneeId', this.auth.employee.id)
            .where('isComplete', isComplete)
            .page(this.currentPage)
            .get()
            .then(([userTasks, meta]) => {
                if (isComplete) {
                    this.totalCompleteUserTasks = meta.pagination.total;
                    this.completeUserTasks = this.shouldConcat ? [...this.completeUserTasks, ...userTasks] : userTasks;
                } else {
                    this.incompleteUserTasks = this.shouldConcat
                        ? [...this.incompleteUserTasks, ...userTasks]
                        : userTasks;
                }
            })
            .catch(() => {
                this.notify.error('Failed retrieving tasks of user.');
                this.loading.next(false);
                this.changes.next(null);
            });
    }

    private async loadCompanyTasks(options: { isComplete: boolean } = { isComplete: false }): Promise<void> {
        const { isComplete } = options;

        const query = this.baseQuery.clone();
        return query
            .where('isComplete', isComplete)
            .page(this.currentPage)
            .get()
            .then(([companyTasks, meta]) => {
                if (isComplete) {
                    this.totalCompleteCompanyTasks = meta.pagination.total;
                    this.completeCompanyTasks = this.shouldConcat
                        ? [...this.completeCompanyTasks, ...companyTasks]
                        : companyTasks;
                } else {
                    this.totalIncompleteCompanyTasks = meta.pagination.total;
                    this.incompleteCompanyTasks = this.shouldConcat
                        ? [...this.incompleteCompanyTasks, ...companyTasks]
                        : companyTasks;
                }
            })
            .catch(() => {
                this.notify.error('Failed retrieving tasks of user.');
                this.loading.next(false);
                this.changes.next(null);
            });
    }

    private get baseQuery(): QueryFetcher<Task> {
        return Task.param('employee', this.auth.employee.id)
            .where('excludeTerminatedSubjects', true)
            .with([
                'assignee',
                'file',
                'subject',
                'subject.manager',
                'employeeDocument',
                'completedBy',
                'subtasksCount',
                'subtasks',
                'assignees',
                'assignees.manager',
            ]);
    }
}
